<template>
  <sb-banner v-if="showBanner" class="relogin" :variation="bannerVariation">
    <div class="banner-text">
      <div v-html="bannerText" />
      <sb-button v-show="buttonAttrs.show" class="btn" :variation="buttonAttrs.variation" :disabled="buttonAttrs.disabled" @click="handleClick">
        {{ buttonAttrs.text }}
      </sb-button>
    </div>
  </sb-banner>
</template>
<script>
import { SetupGuideEnum } from '@/enums/modules/routes'
import KYCStatus, { KYCInquiryType } from '@/enums/modules/setup/KYCStatus'
import { getDaysUntilDeadline } from '@/utils/common'
import messages from './KYC.messages'

export default {
  name: 'KYCBanner',
  props: {
    currentStatus: {
      type: Number,
      default: null
    },
    completionDeadline: {
      type: String,
      default: null
    },
    isKycApproved: {
      type: Boolean,
      default: false
    },
    hasCreateWroPerm: {
      type: Boolean,
      default: false
    },
    roleId: {
      type: Number,
      default: null
    },
    inquiryTypeId: {
      type: Number,
      default: null
    },
    attempts: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      descriptionMap: {
        [KYCStatus.Created]: {
          type: '',
          message: 'Banner.Notice',
          link: 'Help Center.',
          linkHref: 'https://support.shipbob.com/s/article/Business-Information-Verification',
          CTA: 'Get Started',
          ctaType: 'primary'
        },
        [KYCStatus.Pending]: {
          type: 'warning',
          message: 'Banner.Pending',
          CTA: 'Continue Verification',
          ctaType: 'primary'
        },
        [KYCStatus.Completed]: {
          type: 'info',
          message: 'Banner.Completed'
        },
        [KYCStatus.Expired]: {
          type: 'danger',
          message: 'Banner.Expired',
          CTA: 'Start Business Verification',
          ctaType: 'warning'
        },
        [KYCStatus.Failed]: {
          type: 'danger',
          message: 'Banner.Failed',
          link: 'Help Center.',
          linkHref: 'https://support.shipbob.com/s/article/Business-Information-Verification'
        },
        [KYCStatus.NeedsReview]: {
          type: 'danger',
          message: 'Banner.NeedsReview',
          CTA: 'Upload Documents',
          ctaType: 'primary'
        },
        [KYCStatus.Approved]: {
          type: 'success',
          message: 'Banner.Approved',
          CTA: 'Re-Login',
          ctaType: 'primary'
        },
        [KYCStatus.Declined]: {
          type: 'danger',
          message: 'Banner.Declined'
        },
        Locked: {
          type: 'danger',
          message: 'Banner.Locked',
          CTA: 'Start Business Verification',
          ctaType: 'warning'
        },
        NonAccountOwner: {
          message: 'Banner.NonAccountOwner'
        },
        NonAccountOwnerLocked: {
          message: 'Banner.NonAccountOwnerLocked'
        }
      }
    }
  },
  computed: {
    isAccountOwner () {
      return this.roleId === 1
    },
    daysUntilDeadline () {
      if (!this.completionDeadline) return null
      return getDaysUntilDeadline(this.completionDeadline)
    },
    noticeType () {
      if (this.isStatusFinal(this.currentStatus)) return this.currentStatus
      if (this.isDeadlinePassed()) return this.isAccountOwner ? 'Locked' : 'NonAccountOwnerLocked'
      return this.isAccountOwner ? this.currentStatus : 'NonAccountOwner'
    },
    bannerText () {
      let messagePath = this.descriptionMap[this.noticeType]?.message
      const currentStatusMessage = this.descriptionMap[this.currentStatus]?.message

      if (this.noticeType === 'Locked' && this.$te(`${currentStatusMessage}Locked`)) {
        messagePath = `${currentStatusMessage}Locked`
      }

      return this.$t(messagePath, { daysUntilDeadline: this.daysUntilDeadline })
    },
    buttonAttrs () {
      const { CTA, ctaType } = this.descriptionMap[this.noticeType] || {}
      const isApproved = this.currentStatus === KYCStatus.Approved
      return {
        text: CTA,
        variation: this.daysUntilDeadline != null && this.daysUntilDeadline <= 7 && !isApproved ? 'warning' : ctaType,
        show: CTA != null && this.isAccountOwner,
        disabled: this.currentStatus === KYCStatus.Expired && this.attempts > 3
      }
    },
    bannerVariation () {
      const isDeclined = this.isStatusDeclined(this.currentStatus)
      const isCompleted = this.isStatusCompleted(this.currentStatus)
      if (isCompleted) return 'warning'
      if ((this.daysUntilDeadline !== null && this.daysUntilDeadline <= 7) || isDeclined) return 'danger'
      if ((this.daysUntilDeadline !== null && this.daysUntilDeadline <= 15) || isCompleted) return 'warning'
      return 'info'
    },
    showBanner () {
      const isDeclined = this.isStatusDeclined(this.currentStatus) && this.completionDeadline !== null
      const hasDeadline = this.completionDeadline !== null && !this.isStatusFinal(this.currentStatus)
      const isCompleted = this.currentStatus === KYCStatus.Completed && this.completionDeadline !== null && this.daysUntilDeadline <= 0
      const isReloadRequired = this.isKycApproved && !this.hasCreateWroPerm
      return isDeclined || hasDeadline || isReloadRequired || isCompleted
    }
  },
  i18n: { messages },
  methods: {
    handleClick () {
      if (this.isKycApproved && !this.hasCreateWroPerm) {
        return this.$router.push({ name: SetupGuideEnum.Logout })
      }
      this.$emit('cta-clicked')
    },
    isStatusFinal (status) {
      return [KYCStatus.Approved, KYCStatus.Completed, KYCStatus.Failed, KYCStatus.Declined].includes(status)
    },
    isDeadlinePassed () {
      return this.daysUntilDeadline !== null && this.daysUntilDeadline <= 0
    },
    isStatusDeclined (status) {
      const isFailed = status === KYCStatus.Failed && this.inquiryTypeId === KYCInquiryType.Supplementary
      return [KYCStatus.Expired, KYCStatus.Declined].includes(status) || isFailed
    },
    isStatusCompleted (status) {
      return [KYCStatus.Completed, KYCStatus.Approved].includes(status)
    }
  }
}

</script>
<style scoped lang='scss'>
.relogin {
   ::v-deep(.sb-banner-icon),
   ::v-deep(.banner-content) {
     padding-top: 0.2rem;
     padding-bottom: 0.2rem;
   }

   .btn {
    margin: 1em 0 1em 2em !important;
    height: 1.875rem;
    font-weight: 500;
    font-size: 0.75rem !important;
    line-height: 0.9375rem !important;
    text-align: center;
    align-self: center;
    box-shadow: none !important;
   }

  .banner-text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 54px;
  }
}

</style>
